import {
  AccountTree,
  Article,
  AutoAwesome,
  AutoGraph,
  Download,
  Key,
  PieChart
} from "@mui/icons-material";
import { getConfigs } from "../../../configs";
import { ChartSquareBar } from "../../../icons/chart-square-bar";
import { DocumentText } from "../../../icons/document-text";
import { ExclamationCircle } from "../../../icons/exclamation-circle";
import { Users } from "../../../icons/users";
import { PermissionTargets, PermissionTypes } from "../../../store/userState";
import SummarizeIcon from '@mui/icons-material/Summarize';
import TuneIcon from '@mui/icons-material/Tune';
import { FEATURES, ROUTES } from "../../../constants";
import PlaylistAddIcon from '@material-ui/icons/PlaylistAdd';
import { useHasFeature } from "../../../hooks/feature/useHasFeature";
import { useFeatureState } from "../../../store/featureState";

export interface INavBarItem {
  title: string;
  path: string;
  info?: string;
  icon: JSX.Element;
  isNotRelativePath?: boolean;
  queryParams?: () => void;
  experimental?: boolean;
  hideOmnibus?: boolean;
  blacklistCheckParams?: { type: PermissionTypes; target: PermissionTargets };
}
export interface INavBarData {
  title: string;
  experimental?: boolean;
  items: INavBarItem[];
}

const useNavBarData = (): INavBarData[] => {
  const hasJobs = useHasFeature(FEATURES.JOBS)
  const hasReports = useHasFeature(FEATURES.REPORTS)
  const hasGeneratorSets = useHasFeature(FEATURES.GENERATOR_SETS)
  const hasAutomations = useHasFeature(FEATURES.AUTOMATIONS)
  const hasAIPermissions = useHasFeature(FEATURES.AI_TRIAGE)

  const data: INavBarData[] = [
    {
      title: "General",
      experimental: false,
      items: [
        {
          title: "Overview",
          path: "/",
          info: "",
          icon: <PieChart fontSize="small" />,
          experimental: false,
        },
        {
          title: "Projects",
          path: "/projects/",
          info: "",
          icon: <AccountTree fontSize="small" />,
          experimental: false,
        },
      ],
    },
    {
      title: "Analysis",
      experimental: false,
      items: [
        {
          title: "Issues",
          path: "/issues",
          info: "",
          icon: <ExclamationCircle fontSize="small" />,
          experimental: false,
        },
        {
          title: "Runs",
          path: "/runs",
          info: "",
          icon: <ChartSquareBar fontSize="small" />,
          experimental: false,
        },
      ],
    },
    {
      title: "Manage",
      experimental: false,
      items: [
        {
          title: "Configurations",
          path: "/configurations",
          icon: <DocumentText fontSize="small" />,
          experimental: false,
          blacklistCheckParams: {
            type: PermissionTypes.read,
            target: PermissionTargets.job,
          },
        },
        hasAutomations && {
          title: "Automations",
          path: "/automations",
          icon: <AutoAwesome fontSize="small" />,
          experimental: false,
        },
        hasAIPermissions && {
          title: "Embeddings",
          path: "/embeddings",
          icon: <AutoGraph fontSize="small" />,
          experimental: false,
        },
        hasJobs && {
          title: "Jobs",
          path: "/jobs",
          icon: <DocumentText fontSize="small" />,
          experimental: true,
          hideOmnibus: true,
          blacklistCheckParams: {
            type: PermissionTypes.read,
            target: PermissionTargets.job,
          },
        },
        hasReports && {
          title: "Reports",
          path: "/reports",
          icon: <SummarizeIcon fontSize="small" />,
          experimental: false,
        },
        hasGeneratorSets && {
          title: "Patterns",
          path: ROUTES.GENERATOR_SETS,
          icon: <TuneIcon fontSize="small" />,
          experimental: false,
        },
        {
          title: "Policies",
          path: "/policies",
          icon: <PlaylistAddIcon fontSize="small" />,
          experimental: false,
        },
      ].filter((item): item is any => Boolean(item)),
    },
    {
      title: "References",
      experimental: false,
      items: [
        {
          title: "Documentation",
          isNotRelativePath: true,
          path: `${getConfigs().swrlDocsUrl}`,
          icon: <Article fontSize="small" />,
          experimental: false,
        },
        {
          title: "Downloads",
          path: "/downloads",
          icon: <Download fontSize="small" />,
          experimental: false,
          hideOmnibus: false,
        },
      ],
    },
  ]

  return data
}

export default useNavBarData
